import { Injectable } from '@angular/core';

import { getEndpoint } from '@app/utils/get-endpoint';
import { environment } from 'src/environments/environment';

/**
 * Configuration for WebSocket signaling service.
 * Manages endpoint URLs for signaling and token exchange.
 */
export interface SignalingReadiness {
  signalingEndpointConfigured: boolean;
  tokenEndpointConfigured: boolean;
  signalingEndpoint: string;
  tokenEndpoint: string;
}

const DEFAULT_SIGNALING_ENDPOINT =
  'https://signaling-service-np.salmoncliff-e7a50aba.centralus.azurecontainerapps.io';
const DEFAULT_SIGNALING_TOKEN_ENDPOINT = getEndpoint(['signaling', 'signal-token']);

@Injectable({
  providedIn: 'root'
})
export class SignalingConfigService {
  /**
   * Public endpoint URL of the signaling service.
   * Replace with actual deployed URL once available.
   * Example: https://signaling-service-np.azurecontainerapps.io
   */
  private signalingEndpoint: string = this.getSignalingEndpoint();

  /**
   * Backend endpoint that issues short-lived signaling JWT tokens.
   * This avoids exposing signing secrets in the browser.
   */
  private signalingTokenEndpoint: string = this.getSignalingTokenEndpoint();

  constructor() {
    console.log('[SignalingConfig] Initialized', this.getReadiness());
  }

  /**
   * Get the signaling service endpoint URL.
   */
  getEndpoint(): string {
    return this.signalingEndpoint;
  }

  /**
   * Get the backend endpoint used for requesting signaling JWT tokens.
   */
  getTokenEndpoint(): string {
    return this.signalingTokenEndpoint;
  }

  /**
   * Get the full WebSocket URL with protocol.
   * Converts HTTPS endpoint to WSS (WebSocket Secure).
   */
  getWebSocketUrl(): string {
    const endpoint = this.signalingEndpoint;
    if (!endpoint) {
      console.warn('[SignalingConfig] No signaling endpoint configured');
      return '';
    }
    // Convert https:// to wss://
    return endpoint.replace(/^https:\/\//, 'wss://').replace(/\/$/, '');
  }

  /**
   * Resolve signaling endpoint from environment or fallback.
   */
  private getSignalingEndpoint(): string {
    const runtimeWindow = window as unknown as { SIGNALING_ENDPOINT?: string };
    if (typeof runtimeWindow.SIGNALING_ENDPOINT !== 'undefined') {
      return runtimeWindow.SIGNALING_ENDPOINT;
    }
    if (environment.signalingEndpoint) {
      return environment.signalingEndpoint;
    }
    return DEFAULT_SIGNALING_ENDPOINT;
  }

  /**
   * Resolve signaling token endpoint from environment or fallback.
   */
  private getSignalingTokenEndpoint(): string {
    const runtimeWindow = window as unknown as { SIGNALING_TOKEN_ENDPOINT?: string };
    if (typeof runtimeWindow.SIGNALING_TOKEN_ENDPOINT !== 'undefined') {
      return runtimeWindow.SIGNALING_TOKEN_ENDPOINT;
    }
    if (environment.signalingTokenEndpoint) {
      return environment.signalingTokenEndpoint;
    }
    return DEFAULT_SIGNALING_TOKEN_ENDPOINT;
  }

  /**
   * Set endpoint (for testing or override).
   */
  setEndpoint(endpoint: string): void {
    this.signalingEndpoint = endpoint;
    console.log(`[SignalingConfig] Endpoint updated to: ${endpoint}`);
  }

  /**
   * Set token endpoint (for testing or override).
   */
  setTokenEndpoint(endpoint: string): void {
    this.signalingTokenEndpoint = endpoint;
    console.log(`[SignalingConfig] Token endpoint updated to: ${endpoint}`);
  }

  /**
   * Check if signaling is properly configured.
   */
  isConfigured(): boolean {
    return this.isSignalingEndpointConfigured() && this.isTokenEndpointConfigured();
  }

  /**
   * Returns true when signaling WebSocket endpoint is configured.
   */
  isSignalingEndpointConfigured(): boolean {
    return !!this.signalingEndpoint;
  }

  /**
   * Returns true when backend token endpoint is configured.
   */
  isTokenEndpointConfigured(): boolean {
    return !!this.signalingTokenEndpoint;
  }

  /**
   * Structured readiness status used for startup smoke checks.
   */
  getReadiness(): SignalingReadiness {
    return {
      signalingEndpointConfigured: this.isSignalingEndpointConfigured(),
      tokenEndpointConfigured: this.isTokenEndpointConfigured(),
      signalingEndpoint: this.signalingEndpoint,
      tokenEndpoint: this.signalingTokenEndpoint,
    };
  }
}
